import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, 
  Search,
  Plus,
  Edit2,
  Save,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Shield,
  FileText,
  ExternalLink,
  Truck
} from 'lucide-react';

interface Company {
  id: string;
  name: string;
  legal_name: string | null;
  dba_name: string | null;
  company_type: string;
  mc_number: string | null;
  dot_number: string | null;
  email: string | null;
  phone: string | null;
  fax: string | null;
  website: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  // Mailing address
  mailing_address_line1: string | null;
  mailing_address_line2: string | null;
  mailing_city: string | null;
  mailing_state: string | null;
  mailing_postal_code: string | null;
  mailing_country: string | null;
  mailing_phone: string | null;
  mailing_fax: string | null;
  // Credit & general
  credit_score: number | null;
  days_to_pay: number | null;
  credit_grade: string | null;
  credit_rating_label: string | null;
  team_activity_enabled?: boolean | null;
  payment_trend: string | null;
  recommended_exposure: string | null;
  reported_payments: string | null;
  verification_status: string | null;
  operation_type: string | null;
  out_of_interstate_services: boolean | null;
  commodities: string | null;
  special_commodities: string | null;
  memberships: string | null;
  // Authority dates
  authority_expiry_date: string | null;
  insurance_expiry_date: string | null;
  // Authority status
  common_authority_status: string | null;
  contract_authority_status: string | null;
  broker_authority_status: string | null;
  common_authority_pending: boolean | null;
  contract_authority_pending: boolean | null;
  broker_authority_pending: boolean | null;
  // Freight types
  freight_authorized: boolean | null;
  passenger_authorized: boolean | null;
  hhg_authorized: boolean | null;
  // Insurance
  bipd_required: number | null;
  bipd_on_file: number | null;
  cargo_required: boolean | null;
  cargo_on_file: boolean | null;
  bond_required: boolean | null;
  bond_on_file: boolean | null;
  // Insurance carrier
  insurance_policy_number: string | null;
  insurance_coverage_from: number | null;
  insurance_coverage_to: number | null;
  insurance_carrier_name: string | null;
  insurance_contact: string | null;
  insurance_carrier_address: string | null;
  insurance_carrier_phone: string | null;
  insurance_carrier_fax: string | null;
  insurance_effective_date: string | null;
  insurance_cancellation_date: string | null;
  created_at: string | null;
}

type FormData = Omit<Company, 'id' | 'created_at'>;

const emptyCompany: FormData = {
  name: '',
  legal_name: null,
  dba_name: null,
  company_type: 'carrier',
  mc_number: null,
  dot_number: null,
  email: null,
  phone: null,
  fax: null,
  website: null,
  address_line1: null,
  address_line2: null,
  city: null,
  state: null,
  postal_code: null,
  country: 'US',
  mailing_address_line1: null,
  mailing_address_line2: null,
  mailing_city: null,
  mailing_state: null,
  mailing_postal_code: null,
  mailing_country: 'US',
  mailing_phone: null,
  mailing_fax: null,
  credit_score: null,
  days_to_pay: null,
  credit_grade: null,
  credit_rating_label: null,
  team_activity_enabled: false,
  payment_trend: null,
  recommended_exposure: null,
  reported_payments: null,
  verification_status: 'pending',
  operation_type: null,
  out_of_interstate_services: false,
  commodities: null,
  special_commodities: null,
  memberships: null,
  authority_expiry_date: null,
  insurance_expiry_date: null,
  common_authority_status: null,
  contract_authority_status: null,
  broker_authority_status: null,
  common_authority_pending: false,
  contract_authority_pending: false,
  broker_authority_pending: false,
  freight_authorized: true,
  passenger_authorized: false,
  hhg_authorized: false,
  bipd_required: 0,
  bipd_on_file: 0,
  cargo_required: false,
  cargo_on_file: false,
  bond_required: false,
  bond_on_file: false,
  insurance_policy_number: null,
  insurance_coverage_from: 0,
  insurance_coverage_to: 75000,
  insurance_carrier_name: null,
  insurance_contact: null,
  insurance_carrier_address: null,
  insurance_carrier_phone: null,
  insurance_carrier_fax: null,
  insurance_effective_date: null,
  insurance_cancellation_date: null,
};

export default function AdminCompanies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyCompany);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCompanies((data || []) as Company[]);
    } catch (error) {
      console.error('Error loading companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingCompany(null);
    setFormData(emptyCompany);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (company: Company) => {
    setEditingCompany(company);
    const { id, created_at, ...rest } = company;
    setFormData(rest);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Company name is required');
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        company_type: formData.company_type as 'carrier' | 'broker' | 'shipper' | 'factoring',
        verification_status: formData.verification_status as 'pending' | 'in_review' | 'verified' | 'rejected' | 'expired',
        credit_score: formData.credit_score ? Number(formData.credit_score) : null,
        days_to_pay: formData.days_to_pay ? Number(formData.days_to_pay) : null,
        bipd_required: formData.bipd_required ? Number(formData.bipd_required) : 0,
        bipd_on_file: formData.bipd_on_file ? Number(formData.bipd_on_file) : 0,
        insurance_coverage_from: formData.insurance_coverage_from ? Number(formData.insurance_coverage_from) : 0,
        insurance_coverage_to: formData.insurance_coverage_to ? Number(formData.insurance_coverage_to) : 0,
      };

      if (editingCompany) {
        const { error } = await supabase
          .from('companies')
          .update(dataToSave)
          .eq('id', editingCompany.id);

        if (error) throw error;
        toast.success('Company updated successfully');
      } else {
        const { error } = await supabase
          .from('companies')
          .insert([dataToSave]);

        if (error) throw error;
        toast.success('Company created successfully');
      }

      setIsModalOpen(false);
      loadCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
      toast.error('Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const updateFormField = (field: keyof FormData, value: string | number | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getCreditScoreColor = (score: number | null) => {
    if (score === null) return 'text-muted-foreground';
    if (score >= 80) return 'text-primary';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  const filteredCompanies = companies.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.legal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.mc_number?.includes(searchTerm) ||
    c.dot_number?.includes(searchTerm) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Companies</h1>
            <p className="text-[#6b7280] mt-1">Manage company profiles, credit scores, and verification</p>
          </div>
          <Button onClick={handleOpenCreate} className="bg-lr-cyan hover:bg-lr-cyan/90">
            <Plus className="w-4 h-4 mr-2" />
            Add Company
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280]" />
            <Input
              placeholder="Search by name, MC, DOT, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#111820] border-[#1e2530] text-white"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lr-cyan"></div>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredCompanies.length === 0 ? (
              <Card className="bg-[#111820] border-[#1e2530]">
                <CardContent className="py-12 text-center">
                  <Building2 className="w-12 h-12 text-[#6b7280] mx-auto mb-4" />
                  <p className="text-[#6b7280]">No companies found</p>
                </CardContent>
              </Card>
            ) : (
              filteredCompanies.map((company) => (
                <Card key={company.id} className="bg-[#111820] border-[#1e2530]">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-lr-cyan/10 flex items-center justify-center shrink-0">
                          <Building2 className="w-6 h-6 text-lr-cyan" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-white font-semibold">{company.name}</h3>
                            <Badge variant="outline" className="text-xs capitalize">
                              {company.company_type}
                            </Badge>
                            {company.verification_status === 'verified' && (
                              <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">
                                <Shield className="w-3 h-3 mr-1" />
                                Verified
                              </Badge>
                            )}
                          </div>
                          {company.legal_name && company.legal_name !== company.name && (
                            <p className="text-xs text-[#6b7280] mt-0.5">Legal: {company.legal_name}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-[#6b7280] flex-wrap">
                            {company.mc_number && (
                              <a 
                                href={`https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=MC_MX&query_string=${company.mc_number}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-lr-cyan hover:underline flex items-center gap-1"
                              >
                                MC{company.mc_number}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                            {company.dot_number && (
                              <a 
                                href={`https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=USDOT&query_string=${company.dot_number}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-lr-cyan hover:underline flex items-center gap-1"
                              >
                                DOT{company.dot_number}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                            {company.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {company.email}
                              </span>
                            )}
                            {company.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {company.phone}
                              </span>
                            )}
                            {company.city && company.state && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {company.city}, {company.state}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-right">
                          <p className="text-xs text-[#6b7280]">Credit Score</p>
                          <p className={`text-lg font-bold ${getCreditScoreColor(company.credit_score)}`}>
                            {company.credit_score ?? 'N/A'}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenEdit(company)}
                          className="border-[#1e2530] hover:bg-[#1e2530]"
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Edit/Create Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#111820] border-[#1e2530]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white">
                <Building2 className="w-5 h-5 text-lr-cyan" />
                {editingCompany ? 'Edit Company' : 'Add New Company'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 mt-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Company Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => updateFormField('name', e.target.value)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="Enter company name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Legal Name</Label>
                    <Input
                      value={formData.legal_name || ''}
                      onChange={(e) => updateFormField('legal_name', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="Legal business name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">DBA Name</Label>
                    <Input
                      value={formData.dba_name || ''}
                      onChange={(e) => updateFormField('dba_name', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="Doing business as"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Company Type</Label>
                    <Select
                      value={formData.company_type}
                      onValueChange={(v) => updateFormField('company_type', v)}
                    >
                      <SelectTrigger className="bg-[#1e2530] border-[#2a3441] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="carrier">Carrier</SelectItem>
                        <SelectItem value="broker">Broker</SelectItem>
                        <SelectItem value="shipper">Shipper</SelectItem>
                        <SelectItem value="factoring">Factoring</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Verification Status</Label>
                    <Select
                      value={formData.verification_status || 'pending'}
                      onValueChange={(v) => updateFormField('verification_status', v)}
                    >
                      <SelectTrigger className="bg-[#1e2530] border-[#2a3441] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_review">In Review</SelectItem>
                        <SelectItem value="verified">Verified</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Operation Type</Label>
                    <Select
                      value={formData.operation_type || ''}
                      onValueChange={(v) => updateFormField('operation_type', v || null)}
                    >
                      <SelectTrigger className="bg-[#1e2530] border-[#2a3441] text-white">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="interstate">Interstate</SelectItem>
                        <SelectItem value="intrastate">Intrastate</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                        <SelectItem value="unknown">Unknown</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Memberships</Label>
                    <Input
                      value={formData.memberships || ''}
                      onChange={(e) => updateFormField('memberships', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="TIA, OOIDA, etc."
                    />
                  </div>
                </div>
              </div>

              <Separator className="bg-[#1e2530]" />

              {/* FMCSA & Credit */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  FMCSA & Credit Information
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">MC Number</Label>
                    <Input
                      value={formData.mc_number || ''}
                      onChange={(e) => updateFormField('mc_number', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="123456"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">DOT Number</Label>
                    <Input
                      value={formData.dot_number || ''}
                      onChange={(e) => updateFormField('dot_number', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="1234567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Team Activity</Label>
                    <label className="flex items-center gap-2 h-10 text-white text-sm">
                      <Checkbox
                        checked={!!formData.team_activity_enabled}
                        onCheckedChange={(v) => updateFormField('team_activity_enabled', v === true)}
                      />
                      Enabled for this company
                    </label>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Credit Score (0-100)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.credit_score ?? ''}
                      onChange={(e) => updateFormField('credit_score', e.target.value ? Number(e.target.value) : null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="97"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Days to Pay</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.days_to_pay ?? ''}
                      onChange={(e) => updateFormField('days_to_pay', e.target.value ? Number(e.target.value) : null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Credit Grade</Label>
                    <Input
                      value={formData.credit_grade || ''}
                      onChange={(e) => updateFormField('credit_grade', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="A+"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Rating Label</Label>
                    <Input
                      value={formData.credit_rating_label || ''}
                      onChange={(e) => updateFormField('credit_rating_label', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="Strong"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Payment Trend</Label>
                    <Input
                      value={formData.payment_trend || ''}
                      onChange={(e) => updateFormField('payment_trend', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="Stable"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Recommended Exposure</Label>
                    <Input
                      value={formData.recommended_exposure || ''}
                      onChange={(e) => updateFormField('recommended_exposure', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="$75k or -"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Reported Payments</Label>
                    <Input
                      value={formData.reported_payments || ''}
                      onChange={(e) => updateFormField('reported_payments', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="100+"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Commodities</Label>
                    <Input
                      value={formData.commodities || ''}
                      onChange={(e) => updateFormField('commodities', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="General freight, produce, etc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Special Commodities</Label>
                    <Input
                      value={formData.special_commodities || ''}
                      onChange={(e) => updateFormField('special_commodities', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="Hazmat, oversized, etc."
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="out_of_interstate"
                    checked={formData.out_of_interstate_services || false}
                    onCheckedChange={(v) => updateFormField('out_of_interstate_services', !!v)}
                  />
                  <Label htmlFor="out_of_interstate" className="text-[#9ca3af]">Out of Interstate Services</Label>
                </div>
              </div>

              <Separator className="bg-[#1e2530]" />

              {/* Authority Status */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  DOT Authority Status
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Common Authority</Label>
                    <Select
                      value={formData.common_authority_status || ''}
                      onValueChange={(v) => updateFormField('common_authority_status', v || null)}
                    >
                      <SelectTrigger className="bg-[#1e2530] border-[#2a3441] text-white">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="none">None</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2 mt-1">
                      <Checkbox
                        id="common_pending"
                        checked={formData.common_authority_pending || false}
                        onCheckedChange={(v) => updateFormField('common_authority_pending', !!v)}
                      />
                      <Label htmlFor="common_pending" className="text-xs text-[#6b7280]">Pending</Label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Contract Authority</Label>
                    <Select
                      value={formData.contract_authority_status || ''}
                      onValueChange={(v) => updateFormField('contract_authority_status', v || null)}
                    >
                      <SelectTrigger className="bg-[#1e2530] border-[#2a3441] text-white">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="none">None</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2 mt-1">
                      <Checkbox
                        id="contract_pending"
                        checked={formData.contract_authority_pending || false}
                        onCheckedChange={(v) => updateFormField('contract_authority_pending', !!v)}
                      />
                      <Label htmlFor="contract_pending" className="text-xs text-[#6b7280]">Pending</Label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Broker Authority</Label>
                    <Select
                      value={formData.broker_authority_status || ''}
                      onValueChange={(v) => updateFormField('broker_authority_status', v || null)}
                    >
                      <SelectTrigger className="bg-[#1e2530] border-[#2a3441] text-white">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="none">None</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2 mt-1">
                      <Checkbox
                        id="broker_pending"
                        checked={formData.broker_authority_pending || false}
                        onCheckedChange={(v) => updateFormField('broker_authority_pending', !!v)}
                      />
                      <Label htmlFor="broker_pending" className="text-xs text-[#6b7280]">Pending</Label>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="freight"
                      checked={formData.freight_authorized || false}
                      onCheckedChange={(v) => updateFormField('freight_authorized', !!v)}
                    />
                    <Label htmlFor="freight" className="text-[#9ca3af]">Freight</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="passenger"
                      checked={formData.passenger_authorized || false}
                      onCheckedChange={(v) => updateFormField('passenger_authorized', !!v)}
                    />
                    <Label htmlFor="passenger" className="text-[#9ca3af]">Passenger</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="hhg"
                      checked={formData.hhg_authorized || false}
                      onCheckedChange={(v) => updateFormField('hhg_authorized', !!v)}
                    />
                    <Label htmlFor="hhg" className="text-[#9ca3af]">HHG</Label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Authority Expiry Date</Label>
                    <Input
                      type="date"
                      value={formData.authority_expiry_date || ''}
                      onChange={(e) => updateFormField('authority_expiry_date', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Insurance Expiry Date</Label>
                    <Input
                      type="date"
                      value={formData.insurance_expiry_date || ''}
                      onChange={(e) => updateFormField('insurance_expiry_date', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                </div>
              </div>

              <Separator className="bg-[#1e2530]" />

              {/* Insurance Requirements */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  DOT Insurance Requirements
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">BIPD Required ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.bipd_required ?? ''}
                      onChange={(e) => updateFormField('bipd_required', e.target.value ? Number(e.target.value) : 0)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">BIPD On File ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.bipd_on_file ?? ''}
                      onChange={(e) => updateFormField('bipd_on_file', e.target.value ? Number(e.target.value) : 0)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="cargo_req"
                        checked={formData.cargo_required || false}
                        onCheckedChange={(v) => updateFormField('cargo_required', !!v)}
                      />
                      <Label htmlFor="cargo_req" className="text-[#9ca3af]">Cargo Required</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="cargo_file"
                        checked={formData.cargo_on_file || false}
                        onCheckedChange={(v) => updateFormField('cargo_on_file', !!v)}
                      />
                      <Label htmlFor="cargo_file" className="text-[#9ca3af]">Cargo On File</Label>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="bond_req"
                        checked={formData.bond_required || false}
                        onCheckedChange={(v) => updateFormField('bond_required', !!v)}
                      />
                      <Label htmlFor="bond_req" className="text-[#9ca3af]">Bond Required</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="bond_file"
                        checked={formData.bond_on_file || false}
                        onCheckedChange={(v) => updateFormField('bond_on_file', !!v)}
                      />
                      <Label htmlFor="bond_file" className="text-[#9ca3af]">Bond On File</Label>
                    </div>
                  </div>
                </div>
              </div>

              <Separator className="bg-[#1e2530]" />

              {/* Insurance Carrier Details */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Insurance Carrier Details
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Policy / Surety Number</Label>
                    <Input
                      value={formData.insurance_policy_number || ''}
                      onChange={(e) => updateFormField('insurance_policy_number', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Coverage From ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.insurance_coverage_from ?? ''}
                      onChange={(e) => updateFormField('insurance_coverage_from', e.target.value ? Number(e.target.value) : 0)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Coverage To ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.insurance_coverage_to ?? ''}
                      onChange={(e) => updateFormField('insurance_coverage_to', e.target.value ? Number(e.target.value) : 0)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Insurance Carrier</Label>
                    <Input
                      value={formData.insurance_carrier_name || ''}
                      onChange={(e) => updateFormField('insurance_carrier_name', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Contact</Label>
                    <Input
                      value={formData.insurance_contact || ''}
                      onChange={(e) => updateFormField('insurance_contact', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Carrier Address</Label>
                    <Input
                      value={formData.insurance_carrier_address || ''}
                      onChange={(e) => updateFormField('insurance_carrier_address', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Carrier Phone</Label>
                    <Input
                      value={formData.insurance_carrier_phone || ''}
                      onChange={(e) => updateFormField('insurance_carrier_phone', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Carrier Fax</Label>
                    <Input
                      value={formData.insurance_carrier_fax || ''}
                      onChange={(e) => updateFormField('insurance_carrier_fax', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Effective Date</Label>
                    <Input
                      type="date"
                      value={formData.insurance_effective_date || ''}
                      onChange={(e) => updateFormField('insurance_effective_date', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Cancellation Date</Label>
                    <Input
                      type="date"
                      value={formData.insurance_cancellation_date || ''}
                      onChange={(e) => updateFormField('insurance_cancellation_date', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                </div>
              </div>

              <Separator className="bg-[#1e2530]" />

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Business Contact
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Phone</Label>
                    <Input
                      value={formData.phone || ''}
                      onChange={(e) => updateFormField('phone', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Fax</Label>
                    <Input
                      value={formData.fax || ''}
                      onChange={(e) => updateFormField('fax', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="(555) 123-4568"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Email</Label>
                    <Input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => updateFormField('email', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Website</Label>
                    <Input
                      value={formData.website || ''}
                      onChange={(e) => updateFormField('website', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                      placeholder="www.company.com"
                    />
                  </div>
                </div>
              </div>

              <Separator className="bg-[#1e2530]" />

              {/* Business Address */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Business Address
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Address Line 1</Label>
                    <Input
                      value={formData.address_line1 || ''}
                      onChange={(e) => updateFormField('address_line1', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Address Line 2</Label>
                    <Input
                      value={formData.address_line2 || ''}
                      onChange={(e) => updateFormField('address_line2', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label className="text-[#9ca3af]">City</Label>
                    <Input
                      value={formData.city || ''}
                      onChange={(e) => updateFormField('city', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">State</Label>
                    <Input
                      value={formData.state || ''}
                      onChange={(e) => updateFormField('state', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Postal Code</Label>
                    <Input
                      value={formData.postal_code || ''}
                      onChange={(e) => updateFormField('postal_code', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                </div>
              </div>

              <Separator className="bg-[#1e2530]" />

              {/* Mailing Address */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Mailing Address
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Address Line 1</Label>
                    <Input
                      value={formData.mailing_address_line1 || ''}
                      onChange={(e) => updateFormField('mailing_address_line1', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Address Line 2</Label>
                    <Input
                      value={formData.mailing_address_line2 || ''}
                      onChange={(e) => updateFormField('mailing_address_line2', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label className="text-[#9ca3af]">City</Label>
                    <Input
                      value={formData.mailing_city || ''}
                      onChange={(e) => updateFormField('mailing_city', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">State</Label>
                    <Input
                      value={formData.mailing_state || ''}
                      onChange={(e) => updateFormField('mailing_state', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Postal Code</Label>
                    <Input
                      value={formData.mailing_postal_code || ''}
                      onChange={(e) => updateFormField('mailing_postal_code', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Mailing Phone</Label>
                    <Input
                      value={formData.mailing_phone || ''}
                      onChange={(e) => updateFormField('mailing_phone', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9ca3af]">Mailing Fax</Label>
                    <Input
                      value={formData.mailing_fax || ''}
                      onChange={(e) => updateFormField('mailing_fax', e.target.value || null)}
                      className="bg-[#1e2530] border-[#2a3441] text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="border-[#1e2530] hover:bg-[#1e2530]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-lr-cyan hover:bg-lr-cyan/90"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {editingCompany ? 'Update Company' : 'Create Company'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
